import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import DashboardLayout from "@/components/layout/DashboardLayout";

import ShopifyRoutes from "@/routes/ShopifyRoutes";

import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import StoreConnectPage from "@/pages/StoreConnectPage";
import ShopifyConnectPage from "@/pages/ShopifyConnectPage";
import CompetitorsPage from "@/pages/CompetitorsPage";
import CompetitorDetailPage from "@/pages/CompetitorDetailPage";
import AlertsPage from "@/pages/AlertsPage";
import ProductIntelligencePage from "@/pages/ProductIntelligencePage";
import ScalingSignalsPage from "@/pages/ScalingSignalsPage";
import ScriptGeneratorPage from "@/pages/ScriptGeneratorPage";

import MetaAdsSpyToolPage from "@/pages/marketing/MetaAdsSpyToolPage";
import TikTokAdsSpyToolPage from "@/pages/marketing/TikTokAdsSpyToolPage";
import MetaAdsLibraryAltPage from "@/pages/marketing/MetaAdsLibraryAltPage";
import AdScriptGeneratorPage from "@/pages/marketing/AdScriptGeneratorPage";
import GoogleAdsAnalysisPage from "@/pages/marketing/GoogleAdsAnalysisPage";
import MetaAdsSearchPage from "@/pages/tools/MetaAdsSearchPage";
import TikTokAdsSearchPage from "@/pages/tools/TikTokAdsSearchPage";
import GoogleAdsSearchPage from "@/pages/tools/GoogleAdsSearchPage";
import HowToUseMetaAdsLibraryPage from "@/pages/guides/HowToUseMetaAdsLibraryPage";

import SolutionsHubPage from "@/pages/marketing/SolutionsHubPage";
import SolutionChannelPage from "@/pages/marketing/SolutionChannelPage";
import WaitlistPage from "@/pages/marketing/WaitlistPage";

import BlogIndexPage from "@/pages/blog/BlogIndexPage";
import BlogLegacyRedirect from "@/pages/blog/BlogLegacyRedirect";
import BlogRootSlugGate from "@/pages/blog/BlogRootSlugGate";

import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";
import GdprPage from "@/pages/legal/GdprPage";
import ContactPage from "@/pages/ContactPage";
import ScrollToTop from "@/components/ScrollToTop";

import AdminGuard from "@/auth/AdminGuard";
import AdminRoutes from "@/routes/AdminRoutes";

const PlaceholderPage = ({ title, description }) => (
  <div className="p-6">
    <div className="max-w-2xl mx-auto text-center py-12">
      <h1 className="text-2xl font-heading font-bold mb-4">{title}</h1>
      <p className="text-muted-foreground mb-6">{description}</p>
      <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-secondary text-secondary-foreground">
        Coming Soon
      </div>
    </div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/** Hostnames that always use the marketing landing at `/` (not login), even if REACT_APP_HOME=login. */
function isMarketingLandingHostname() {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const list = (process.env.REACT_APP_LANDING_HOSTNAMES || "radar.aquivolabs.com")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return list.some((d) => host === d || host.endsWith(`.${d}`));
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppContent() {
  const { user } = useAuth();
  const basePath = process.env.REACT_APP_BASE_PATH || "/";

  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get("shop");
  const host = urlParams.get("host");
  const isShopifyEmbedded = Boolean(shop && host);

  const shouldRedirectToDashboard = user && isShopifyEmbedded && window.location.pathname === "/";
  const useLoginAsHome = process.env.REACT_APP_HOME === "login" && !isMarketingLandingHostname();

  return (
    <BrowserRouter basename={basePath}>
      <ScrollToTop />
      <Routes>
        <Route
          path="/"
          element={
            shouldRedirectToDashboard ? (
              <Navigate to={`/dashboard?shop=${shop}&host=${host}`} replace />
            ) : useLoginAsHome ? (
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            ) : (
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            )
          }
        />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        <Route path="/shopify-connect" element={<ShopifyConnectPage />} />

        <Route path="/shopify/*" element={<ShopifyRoutes />} />
        <Route path="/auth/shopify/*" element={<ShopifyRoutes />} />

        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="competitors" element={<CompetitorsPage />} />
          <Route path="competitors/:idSlug" element={<CompetitorDetailPage />} />
          <Route path="competitors/add" element={<PlaceholderPage title="Add Competitor" description="Add a new competitor to track" />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="analytics" element={<ScalingSignalsPage />} />
          <Route path="analytics/products" element={<ProductIntelligencePage />} />
          <Route path="analytics/report" element={<PlaceholderPage title="Generate Report" description="Create custom intelligence reports" />} />
          <Route path="store-connect" element={<StoreConnectPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="script-generator" element={<ScriptGeneratorPage />} />
          <Route path="help" element={<PlaceholderPage title="Help & Support" description="Get help with RADAR Intelligence Platform" />} />
        </Route>

        <Route path="/meta-ads-spy-tool" element={<MetaAdsSpyToolPage />} />
        <Route path="/tiktok-ads-spy-tool" element={<TikTokAdsSpyToolPage />} />
        <Route path="/meta-ads-library-alternative" element={<MetaAdsLibraryAltPage />} />
        <Route path="/ad-script-generator" element={<AdScriptGeneratorPage />} />
        <Route path="/google-ads-competitor-analysis" element={<GoogleAdsAnalysisPage />} />

        <Route path="/tools/meta-ads-search" element={<MetaAdsSearchPage />} />
        <Route path="/tools/tiktok-ads-search" element={<TikTokAdsSearchPage />} />
        <Route path="/tools/google-ads-search" element={<GoogleAdsSearchPage />} />

        <Route path="/guides/how-to-use-meta-ads-library" element={<HowToUseMetaAdsLibraryPage />} />

        <Route path="/solutions" element={<SolutionsHubPage />} />
        <Route path="/solutions/meta-ads" element={<SolutionChannelPage channelKey="meta" />} />
        <Route path="/solutions/tiktok-ads" element={<SolutionChannelPage channelKey="tiktok" />} />
        <Route path="/solutions/google-ads" element={<SolutionChannelPage channelKey="google" />} />
        <Route path="/solutions/competitor-pricing" element={<SolutionChannelPage channelKey="pricing" />} />

        <Route path="/waitlist" element={<Navigate to="/waitlist/intelligence" replace />} />
        <Route path="/waitlist/intelligence" element={<WaitlistPage variant="intelligence" />} />
        <Route path="/waitlist/research" element={<WaitlistPage variant="research" />} />

        {/* Legacy redirects */}
        <Route path="/waitlist/google" element={<Navigate to="/waitlist/intelligence" replace />} />
        <Route path="/waitlist/reddit" element={<Navigate to="/waitlist/intelligence" replace />} />
        <Route path="/waitlist/x" element={<Navigate to="/waitlist/intelligence" replace />} />

        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogLegacyRedirect />} />

        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/gdpr" element={<GdprPage />} />

        {/* Operator console — must be declared BEFORE the /:slug catch-all
            below, or BlogRootSlugGate would intercept /admin. */}
        <Route
          path="/admin/*"
          element={
            <AdminGuard>
              <AdminRoutes />
            </AdminGuard>
          }
        />

        <Route path="/:slug" element={<BlogRootSlugGate />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return <AppContent />;
}

export default App;
